import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Input, Button, Select, Tag, Avatar, Drawer, Form, notification,
  InputNumber, DatePicker, Modal, Slider, Spin, Badge,
} from 'antd';
import {
  SearchOutlined, UserOutlined, PhoneOutlined, ShopOutlined,
  EnvironmentOutlined, GlobalOutlined, CameraOutlined, LoadingOutlined,
  EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined, LogoutOutlined,
  UnorderedListOutlined, CheckCircleOutlined, AppstoreOutlined,
  CloseCircleOutlined, ClockCircleOutlined, ScissorOutlined,
  ArrowLeftOutlined, FilterOutlined, BankOutlined, CalendarOutlined,
  MailOutlined, TagsOutlined, RightOutlined, DownOutlined,
  ShareAltOutlined, CopyOutlined, WhatsAppOutlined, LinkOutlined,
} from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import axios from 'axios';
import moment from 'moment';
import { baseUrl } from '../../config';
import { hasPermission } from '../../components/permissions';

const { Option } = Select;
const { TextArea } = Input;
const { Item } = Form;

// ── Auth header ───────────────────────────────────────────────────────────────
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

// ── Crop helpers ──────────────────────────────────────────────────────────────
const createImg = (url) => new Promise((res, rej) => {
  const img = new Image();
  img.addEventListener('load', () => res(img));
  img.addEventListener('error', rej);
  img.setAttribute('crossOrigin', 'anonymous');
  img.src = url;
});

async function getCroppedBlob(src, crop) {
  const img = await createImg(src);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const max = 900;
  const sc = Math.min(max / crop.width, max / crop.height, 1);
  canvas.width = Math.round(crop.width * sc);
  canvas.height = Math.round(crop.height * sc);
  ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
  return new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.85));
}

async function uploadBlob(blob, type) {
  const fd = new FormData();
  fd.append('photo', blob, 'photo.jpg');
  const res = await fetch(`${baseUrl}/admin/business-directory/upload-photo?type=${type}`, { method: 'POST', headers: authHeader(), body: fd });
  const d = await res.json();
  if (!d.status) throw new Error(d.message || 'Upload failed');
  return d.url;
}

// ── Crop Modal ────────────────────────────────────────────────────────────────
function CropModal({ open, imageSrc, aspect, onDone, onCancel }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const onComplete = useCallback((_, p) => setPixels(p), []);

  const apply = async () => {
    if (!pixels) return;
    setBusy(true);
    try { onDone(await getCroppedBlob(imageSrc, pixels)); }
    catch { notification.error({ message: 'Crop विफल हुआ।', placement: 'top' }); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onCancel={onCancel} width="100%" style={{ top: 0, margin: 0, maxWidth: 420, padding: 0 }}
      centered destroyOnClose
      title={<span><ScissorOutlined style={{ marginRight: 6, color: '#d4a84b' }} />फोटो काटें</span>}
      footer={[
        <Button key="c" onClick={onCancel}>रद्द</Button>,
        <Button key="a" type="primary" loading={busy} onClick={apply}
          style={{ background: '#7a0000', borderColor: '#7a0000' }}>✓ सेट करें</Button>,
      ]}>
      <div style={{ position: 'relative', height: 260, background: '#111', borderRadius: 8, overflow: 'hidden' }}>
        {imageSrc && <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={aspect}
          onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />}
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>ज़ूम</div>
        <Slider min={1} max={3} step={0.05} value={zoom} onChange={setZoom}
          trackStyle={{ background: '#7a0000' }} handleStyle={{ borderColor: '#7a0000' }} />
      </div>
    </Modal>
  );
}

// ── Profile photo upload ──────────────────────────────────────────────────────
function PhotoUpload({ value, onChange }) {
  const [preview, setPreview] = useState(value || null);
  const [uploading, setUploading] = useState(false);
  const [rawSrc, setRawSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const ref = useRef(null);

  const handleFile = (file) => {
    if (!file || !/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) return;
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (ref.current) ref.current.value = '';
  };

  const onCropDone = async (blob) => {
    setCropOpen(false); setUploading(true);
    try { const url = await uploadBlob(blob, 'profile'); setPreview(url); onChange(url); }
    catch (e) { notification.error({ message: e.message, placement: 'top' }); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files[0])} />
      <div style={{ position: 'relative', width: 88, height: 88 }}>
        <div onClick={() => !uploading && ref.current?.click()}
          style={{ width: 88, height: 88, borderRadius: 10, border: '3px solid #7a0000',
            overflow: 'hidden', cursor: 'pointer', background: '#f0f2f5', position: 'relative' }}>
          {preview
            ? <img src={preview} alt="p" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                {uploading ? <LoadingOutlined style={{ fontSize: 26, color: '#7a0000' }} />
                  : <UserOutlined style={{ fontSize: 34, color: '#ccc' }} />}
              </div>
          }
        </div>
        <div onClick={() => !uploading && ref.current?.click()}
          style={{ position: 'absolute', bottom: -4, right: -4, width: 26, height: 26,
            borderRadius: 8, background: '#7a0000', border: '2px solid #fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1 }}>
          <CameraOutlined style={{ color: '#fff', fontSize: 12 }} />
        </div>
        {preview && !uploading && (
          <button type="button"
            onClick={() => { setPreview(null); onChange(null); if (ref.current) ref.current.value = ''; }}
            style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20,
              borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff',
              color: '#fff', fontSize: 10, cursor: 'pointer', lineHeight: '16px',
              textAlign: 'center', padding: 0, zIndex: 2 }}>✕</button>
        )}
      </div>
      <span style={{ fontSize: 10, color: '#aaa', marginTop: 6 }}>
        {preview ? 'बदलने के लिए tap करें' : 'प्रोफाइल फोटो · max 5MB'}
      </span>
      <CropModal open={cropOpen} imageSrc={rawSrc} aspect={1} onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </div>
  );
}

// ── Multi photo upload (2 slots) ──────────────────────────────────────────────
function MultiPhotoUpload({ value, onChange }) {
  const [slots, setSlots] = useState(() => { const u = value ? value.split(',').filter(Boolean) : []; return [u[0]||null, u[1]||null]; });
  const [uploading, setUploading] = useState([false, false]);
  const [rawSrc, setRawSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingIdx, setPendingIdx] = useState(null);
  const refs = [useRef(null), useRef(null)];

  const notify = (s) => onChange(s.filter(Boolean).join(',') || null);

  const handleFile = (file, idx) => {
    if (!file || !/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) return;
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setPendingIdx(idx); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (refs[idx].current) refs[idx].current.value = '';
  };

  const onCropDone = async (blob) => {
    setCropOpen(false);
    const idx = pendingIdx;
    setUploading(u => { const n=[...u]; n[idx]=true; return n; });
    try {
      const url = await uploadBlob(blob, 'business');
      setSlots(s => { const n=[...s]; n[idx]=url; notify(n); return n; });
    } catch (e) { notification.error({ message: e.message, placement: 'top' }); }
    finally { setUploading(u => { const n=[...u]; n[idx]=false; return n; }); }
  };

  const remove = (idx) => {
    setSlots(s => { const n=[...s]; n[idx]=null; notify(n); return n; });
    if (refs[idx].current) refs[idx].current.value = '';
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
        border: '2px dashed #7a0000', borderRadius: 10, padding: '10px 12px',
        background: '#f8faff', minHeight: 90 }}>
        {[0,1].map(idx => (
          <input key={idx} ref={refs[idx]} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0], idx)} />
        ))}
        {[0,1].map(idx => (
          slots[idx] ? (
            <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
              <img src={slots[idx]} alt="" onClick={() => refs[idx].current?.click()}
                style={{ width: 76, height: 76, borderRadius: 8, objectFit: 'cover',
                  border: '2px solid #7a0000', cursor: 'pointer', display: 'block' }} />
              <button type="button" onClick={() => remove(idx)}
                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18,
                  borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff',
                  color: '#fff', fontSize: 9, cursor: 'pointer', lineHeight: '14px',
                  textAlign: 'center', padding: 0 }}>✕</button>
            </div>
          ) : uploading[idx] ? (
            <div key={idx} style={{ width: 76, height: 76, borderRadius: 8,
              border: '2px dashed #7a0000', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LoadingOutlined style={{ fontSize: 20, color: '#7a0000' }} />
            </div>
          ) : (
            <button key={idx} type="button" onClick={() => refs[idx].current?.click()}
              style={{ width: 76, height: 76, borderRadius: 8, border: '2px dashed #7a0000',
                background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0 }}>
              <PlusOutlined style={{ fontSize: 18, color: '#7a0000' }} />
              <span style={{ fontSize: 10, color: '#7a0000' }}>फोटो {idx+1}</span>
            </button>
          )
        ))}
        <div style={{ flex: 1, color: '#bbb', fontSize: 11, lineHeight: 1.8, minWidth: 60 }}>
          दुकान की फोटो<br />JPG/PNG<br />max 5MB
        </div>
      </div>
      <CropModal open={cropOpen} imageSrc={rawSrc} aspect={4/3}
        onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_COLOR = { Pending: 'orange', Verified: 'green', Rejected: 'red' };
const STATUS_ICON  = {
  Verified: <CheckCircleOutlined />, Rejected: <CloseCircleOutlined />, Pending: <ClockCircleOutlined />,
};

const URL_PATTERN  = /(https?:\/\/|www\.|<[^>]+>|javascript:|on\w+=|SELECT |INSERT |DROP |DELETE )/i;
const NAME_PATTERN = /^[a-zA-Zऀ-ॿ\s.''\-]+$/;

const validateName = (label) => (_, v) => {
  if (!v || !v.trim()) return Promise.reject(`${label} दर्ज करें।`);
  if (URL_PATTERN.test(v)) return Promise.reject('अमान्य input');
  if (!NAME_PATTERN.test(v.trim())) return Promise.reject(`${label} में केवल अक्षर लिखें।`);
  return Promise.resolve();
};
const validateSafe = (label) => (_, v) => {
  if (!v || !v.trim()) return Promise.resolve();
  if (URL_PATTERN.test(v)) return Promise.reject(`${label} में link की अनुमति नहीं।`);
  return Promise.resolve();
};
const phoneRules = [
  { required: true, message: 'मोबाइल नंबर आवश्यक है।' },
  { validator: (_, v) => {
    if (!v) return Promise.reject('नंबर दर्ज करें।');
    if (!/^\d+$/.test(v)) return Promise.reject('केवल अंक दर्ज करें।');
    if (v.length !== 10) return Promise.reject('10 अंकों का नंबर दर्ज करें।');
    if (!/^[6-9]/.test(v)) return Promise.reject('6-9 से शुरू होना चाहिए।');
    return Promise.resolve();
  }},
];

// ── Section header ────────────────────────────────────────────────────────────
const Sec = ({ label }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase',
    letterSpacing: '1px', borderBottom: '1px solid #f0f0f0', paddingBottom: 6,
    marginTop: 14, marginBottom: 10 }}>{label}</div>
);

// ── Share helpers ─────────────────────────────────────────────────────────────
const getFormUrl = () => `${window.location.origin}/business-directory-form`;

const getUpdateUrl = () => `${window.location.origin}/business-directory-update`;

const WHATSAPP_MSG = (url) =>
`🙏 *नमस्कार — ABRSVS व्यवसाय निर्देशिका*

अखिल भारतीय राजपुरोहित समाज विकास संस्थान की *व्यवसाय निर्देशिका* में अपना व्यवसाय / नौकरी की जानकारी दर्ज करें।

इससे समाज के लोग आपका व्यवसाय आसानी से ढूंढ सकेंगे।

👉 *नया फॉर्म भरें:*
${url}

✏️ *पहले से दर्ज जानकारी अपडेट करें:*
${getUpdateUrl()}`;

function MobileShareButton() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = getFormUrl();

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  const shareWhatsApp = () =>
    window.open(`https://wa.me/?text=${encodeURIComponent(WHATSAPP_MSG(url))}`, '_blank');

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'ABRSVS व्यवसाय निर्देशिका', url });
      } catch {}
    } else { shareWhatsApp(); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        style={{ background: '#25D366', border: 'none', borderRadius: 8, color: '#fff',
          fontSize: 12, fontWeight: 700, padding: '6px 12px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 5 }}>
        <ShareAltOutlined style={{ fontSize: 13 }} /> Share
      </button>

      <Modal open={open} onCancel={() => setOpen(false)} footer={null} centered
        width="95vw" style={{ maxWidth: 420 }} destroyOnClose
        title={
          <span style={{ fontWeight: 700, color: '#7a0000', fontSize: 14 }}>
            <ShareAltOutlined style={{ marginRight: 6, color: '#25D366' }} />
            फॉर्म शेयर करें
          </span>
        }>

        {/* URL box */}
        <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '10px 12px',
          marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <LinkOutlined style={{ color: '#7a0000', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 11, color: '#333', wordBreak: 'break-all' }}>{url}</span>
          <Button size="small" onClick={copyLink}
            style={{ flexShrink: 0, borderColor: copied ? '#52c41a' : '#7a0000',
              color: copied ? '#52c41a' : '#7a0000', borderRadius: 6, fontWeight: 600 }}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>

        {/* Message preview */}
        <div style={{ background: '#e7f8ee', borderRadius: 8, padding: '10px 12px',
          marginBottom: 14, fontSize: 12, color: '#1a5c2e',
          whiteSpace: 'pre-line', lineHeight: 1.7, maxHeight: 180, overflowY: 'auto' }}>
          {WHATSAPP_MSG(url)}
        </div>

        {/* Buttons */}
        <Button block icon={<WhatsAppOutlined />} onClick={shareWhatsApp}
          style={{ background: '#25D366', borderColor: '#25D366', color: '#fff',
            borderRadius: 8, fontWeight: 700, height: 46, marginBottom: 8 }}>
          WhatsApp पर शेयर करें
        </Button>
        <Button block icon={<ShareAltOutlined />} onClick={shareNative}
          style={{ borderColor: '#7a0000', color: '#7a0000',
            borderRadius: 8, fontWeight: 600, height: 46 }}>
          Other Apps से शेयर करें
        </Button>

        <div style={{ marginTop: 12, fontSize: 11, color: '#aaa', textAlign: 'center' }}>
          WhatsApp groups, SMS या social media पर शेयर करें
        </div>
      </Modal>
    </>
  );
}

// ── Detail view inside view drawer ────────────────────────────────────────────
function DetailCard({ r }) {
  const bizPhotos = r.business_photo ? r.business_photo.split(',').filter(Boolean) : [];
  const row = (icon, label, val) => val ? (
    <div style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid #f5f5f5' }}>
      <span style={{ color: '#999', width: 18, flexShrink: 0, paddingTop: 2 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: '#aaa' }}>{label}</div>
        <div style={{ fontSize: 13, color: '#1a1a1a', fontWeight: 500 }}>{val}</div>
      </div>
    </div>
  ) : null;

  return (
    <div style={{ paddingBottom: 100 }}>
      {/* Header card */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start',
        background: 'linear-gradient(135deg, #7a0000, #2a4a6b)',
        borderRadius: 12, padding: '16px 14px', color: '#fff', marginBottom: 16 }}>
        <Avatar src={r.profile_photo || undefined}
          icon={!r.profile_photo && <UserOutlined />} size={72} shape="square"
          style={{ borderRadius: 10, border: '3px solid #d4a84b', flexShrink: 0,
            background: r.profile_photo ? undefined : '#2a4a6b' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{r.name}</div>
          {r.father_name && <div style={{ fontSize: 12, color: '#d4a84b', marginTop: 2 }}>S/o {r.father_name}</div>}
          {r.gotra && <div style={{ fontSize: 11, color: '#b0c4de' }}>गोत्र: {r.gotra}</div>}
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Tag color={STATUS_COLOR[r.verification_status] || 'default'}
              icon={STATUS_ICON[r.verification_status] || STATUS_ICON.Pending}
              style={{ fontWeight: 600, fontSize: 11 }}>
              {r.verification_status || 'Pending'}
            </Tag>
            {r.sector_label && <Tag color="blue" style={{ fontSize: 11 }}>{r.sector_label}</Tag>}
          </div>
        </div>
      </div>

      {/* Business photos */}
      {bizPhotos.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Sec label="दुकान की फोटो" />
          <div style={{ display: 'flex', gap: 10 }}>
            {bizPhotos.map((u, i) => (
              <img key={i} src={u} alt="" onClick={() => window.open(u, '_blank')}
                style={{ width: 110, height: 82, objectFit: 'cover', borderRadius: 8,
                  border: '2px solid #d4a84b', cursor: 'pointer' }} />
            ))}
          </div>
        </div>
      )}

      <Sec label="संपर्क" />
      {row(<PhoneOutlined />, 'WhatsApp / मोबाइल', r.phone_no)}
      {row(<PhoneOutlined />, 'वैकल्पिक नंबर', r.alternate_phone)}
      {row(<MailOutlined />, 'ईमेल', r.email)}

      <Sec label="व्यवसाय" />
      {row(<ShopOutlined />, 'फर्म / दुकान', r.firm_name)}
      {row(<BankOutlined />, 'क्षेत्र', [r.sector_label, r.sub_sector_label].filter(Boolean).join(' › '))}
      {row(<CalendarOutlined />, 'स्थापना वर्ष', r.establishment_year ? String(r.establishment_year) : null)}
      {r.business_about && (
        <div style={{ padding: '7px 0', borderBottom: '1px solid #f5f5f5' }}>
          <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>व्यवसाय के बारे में</div>
          <div style={{ fontSize: 13, color: '#222', lineHeight: 1.7,
            background: '#f9f9f9', borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #d4a84b' }}>
            {r.business_about}
          </div>
        </div>
      )}
      {row(<EnvironmentOutlined />, 'व्यावसायिक पता', r.business_address)}

      {(r.current_city || r.state) && (
        <>
          <Sec label="व्यवसाय का शहर" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #f5f5f5' }}>
            <EnvironmentOutlined style={{ color: '#d4a84b', fontSize: 16 }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {[r.current_city, r.state].filter(Boolean).join(', ')}
            </div>
          </div>
        </>
      )}

      <Sec label="मूल स्थान" />
      {row(<EnvironmentOutlined />, 'गाँव / तहसील / जिला', [r.village, r.tehsil, r.district].filter(Boolean).join(', '))}

      {(r.dob || r.gotra) && (
        <>
          <Sec label="व्यक्तिगत" />
          {row(<CalendarOutlined />, 'जन्म तिथि', r.dob && r.dob !== '0000-00-00' ? r.dob : null)}
          {row(<UserOutlined />, 'गोत्र', r.gotra)}
        </>
      )}

      {r.admin_notes && (
        <div style={{ marginTop: 14, background: '#fffbe6', border: '1px solid #ffe58f',
          borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#614700' }}>
          <b>Admin Notes:</b> {r.admin_notes}
        </div>
      )}

      {r.created_at && (
        <div style={{ marginTop: 12, fontSize: 11, color: '#ccc', textAlign: 'right' }}>
          Submitted: {new Date(r.created_at).toLocaleString('en-IN')}
        </div>
      )}
    </div>
  );
}

// ── Submission card ───────────────────────────────────────────────────────────
function EntryCard({ r, onView }) {
  return (
    <div onClick={() => onView(r)}
      style={{ background: '#fff', borderRadius: 12, marginBottom: 10, cursor: 'pointer',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0', overflow: 'hidden' }}>

      {/* Top section: photo + details + View button */}
      <div style={{ display: 'flex', gap: 12, padding: 12, alignItems: 'flex-start' }}>

        {/* Profile photo */}
        <Avatar src={r.profile_photo || undefined}
          icon={!r.profile_photo && <UserOutlined />} size={56} shape="square"
          style={{ borderRadius: 10, border: r.profile_photo ? '2px solid #d4a84b' : '2px solid #e8e8e8',
            background: '#f5f5f5', flexShrink: 0 }} />

        {/* Details */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#7a0000', lineHeight: 1.3 }}>{r.name}</div>
          {r.father_name && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>S/o {r.father_name}</div>
          )}
          {r.phone_no && (
            <div style={{ fontSize: 12, color: '#555', marginTop: 3 }}>
              <PhoneOutlined style={{ marginRight: 4, color: '#d4a84b' }} />{r.phone_no}
            </div>
          )}
          {r.firm_name && (
            <div style={{ fontSize: 12, color: '#444', marginTop: 2 }}>
              <ShopOutlined style={{ marginRight: 4, color: '#d4a84b' }} />{r.firm_name}
              {r.establishment_year ? <span style={{ color: '#aaa', marginLeft: 4 }}>({r.establishment_year})</span> : null}
            </div>
          )}
        </div>

        {/* View button — right side */}
        <button
          onClick={e => { e.stopPropagation(); onView(r); }}
          style={{ flexShrink: 0, background: '#7a0000', border: 'none', borderRadius: 8,
            color: '#fff', fontSize: 12, fontWeight: 600, padding: '6px 12px',
            cursor: 'pointer', alignSelf: 'flex-start', whiteSpace: 'nowrap' }}>
          View
        </button>
      </div>

      {/* Bottom info strip */}
      <div style={{ borderTop: '1px solid #f5f5f5', padding: '8px 12px',
        background: '#fafbfc', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {r.sector_label && (
          <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>{r.sector_label}</Tag>
        )}
        {r.sub_sector_label && (
          <Tag color="geekblue" style={{ fontSize: 10, margin: 0 }}>{r.sub_sector_label}</Tag>
        )}
        {(r.current_city || r.state) && (
          <span style={{ fontSize: 11, color: '#888', display: 'flex', alignItems: 'center', gap: 3 }}>
            <EnvironmentOutlined style={{ fontSize: 10, color: '#d4a84b' }} />
            {[r.current_city, r.state].filter(Boolean).join(', ')}
          </span>
        )}
        {(r.village || r.district) && (
          <span style={{ fontSize: 11, color: '#aaa' }}>
            {[r.village, r.district].filter(Boolean).join(', ')}
          </span>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <Tag color={STATUS_COLOR[r.verification_status] || 'default'}
            style={{ fontSize: 10, margin: 0 }}>
            {r.verification_status || 'Pending'}
          </Tag>
        </div>
      </div>
    </div>
  );
}

// ── Sectors mobile tab ───────────────────────────────────────────────────────
function SectorsMobileTab({ sectors, onRefresh }) {
  const canEdit = hasPermission('/business-directory/sectors', 'editPermission');
  const [expanded, setExpanded]       = useState({});
  const [loading, setLoading]         = useState(false);

  // Sector modal
  const [sectorOpen, setSectorOpen]   = useState(false);
  const [editingSector, setEditSector] = useState(null);
  const [sectorSaving, setSectorSaving] = useState(false);
  const [sectorForm] = Form.useForm();

  // Sub-sector modal
  const [subOpen, setSubOpen]         = useState(false);
  const [editingSub, setEditSub]      = useState(null);
  const [activeSector, setActiveSector] = useState(null);
  const [subSaving, setSubSaving]     = useState(false);
  const [subForm] = Form.useForm();

  const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

  const openAddSector = () => { setEditSector(null); sectorForm.resetFields(); setSectorOpen(true); };
  const openEditSector = (s) => { setEditSector(s); sectorForm.setFieldsValue({ name: s.name, sort_order: s.sort_order }); setSectorOpen(true); };

  const saveSector = async () => {
    try {
      const v = await sectorForm.validateFields(); setSectorSaving(true);
      if (editingSector) await axios.patch(`${baseUrl}/business-sector/update/${editingSector.id}`, v, { headers: hdr() });
      else await axios.post(`${baseUrl}/business-sector/create`, v, { headers: hdr() });
      notification.success({ message: editingSector ? 'Updated' : 'Added', placement: 'top' });
      setSectorOpen(false); onRefresh();
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: e?.response?.data?.message || 'Error', placement: 'top' });
    } finally { setSectorSaving(false); }
  };


  const openAddSub = (sector) => { setEditSub(null); setActiveSector(sector); subForm.resetFields(); setSubOpen(true); };
  const openEditSub = (sub, sector) => { setEditSub(sub); setActiveSector(sector); subForm.setFieldsValue({ name: sub.name, sort_order: sub.sort_order }); setSubOpen(true); };

  const saveSub = async () => {
    try {
      const v = await subForm.validateFields(); setSubSaving(true);
      const payload = { ...v, sector_id: activeSector.id };
      if (editingSub) await axios.patch(`${baseUrl}/business-sector/sub/update/${editingSub.id}`, payload, { headers: hdr() });
      else await axios.post(`${baseUrl}/business-sector/sub/create`, payload, { headers: hdr() });
      notification.success({ message: editingSub ? 'Updated' : 'Added', placement: 'top' });
      setSubOpen(false); onRefresh();
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: e?.response?.data?.message || 'Error', placement: 'top' });
    } finally { setSubSaving(false); }
  };


  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div style={{ padding: '12px 12px 100px' }}>

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#7a0000' }}>Sector Management</div>
          <div style={{ fontSize: 11, color: '#888' }}>{sectors.length} sectors</div>
        </div>
        {canEdit && (
          <Button type="primary" onClick={openAddSector}
            style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8, height: 38 }}>
            + Add Sector
          </Button>
        )}
      </div>

      {/* Sector cards */}
      {sectors.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>कोई sector नहीं</div>
      ) : (
        sectors.map(sector => (
          <div key={sector.id} style={{ background: '#fff', borderRadius: 12, marginBottom: 10,
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0' }}>

            {/* Sector header — name+count row, then buttons row */}
            <div style={{ padding: '12px 14px',
              borderBottom: expanded[sector.id] ? '1px solid #f5f5f5' : 'none' }}>

              {/* Top row: icon + name + expand toggle */}
              <button onClick={() => toggle(sector.id)}
                style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
                  textAlign: 'left', padding: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: '#e8f0f8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AppstoreOutlined style={{ color: '#7a0000', fontSize: 16 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#7a0000' }}>{sector.name}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>
                    {(sector.sub_sectors || []).length} sub-sectors
                  </div>
                </div>
                <span style={{ color: '#aaa', fontSize: 12, flexShrink: 0 }}>
                  {expanded[sector.id] ? <DownOutlined /> : <RightOutlined />}
                </span>
              </button>

            </div>

            {/* Sub-sectors (expanded) */}
            {expanded[sector.id] && (
              <div style={{ padding: '8px 14px 12px' }}>
                {(sector.sub_sectors || []).length === 0 ? (
                  <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', padding: '8px 0' }}>
                    कोई sub-sector नहीं
                  </div>
                ) : (
                  (sector.sub_sectors || []).map(sub => (
                    <div key={sub.id} style={{ background: '#f8f9fb', borderRadius: 8, marginBottom: 8,
                      padding: '8px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <TagsOutlined style={{ color: '#d4a84b', fontSize: 13, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>{sub.name}</span>
                      </div>
                    </div>
                  ))
                )}
                {canEdit && (
                  <Button block size="small" onClick={() => openAddSub(sector)}
                    style={{ marginTop: 4, borderColor: '#d4a84b', color: '#d4a84b',
                      borderRadius: 8, borderStyle: 'dashed', height: 36 }}>
                    + Add Sub-sector
                  </Button>
                )}
              </div>
            )}
          </div>
        ))
      )}

      {/* ── Sector Modal ── */}
      <Modal open={sectorOpen} onCancel={() => setSectorOpen(false)} centered
        title={<span style={{ color: '#7a0000', fontWeight: 700 }}>
          {editingSector ? 'Edit Sector' : 'Add Sector'}
        </span>}
        footer={[
          <Button key="c" onClick={() => setSectorOpen(false)}>रद्द</Button>,
          <Button key="s" type="primary" loading={sectorSaving} onClick={saveSector}
            style={{ background: '#7a0000', borderColor: '#7a0000' }}>
            {editingSector ? 'Update' : 'Save'}
          </Button>,
        ]} destroyOnClose>
        <Form form={sectorForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Sector Name" name="name" rules={[{ required: true, message: 'नाम दर्ज करें' }]}>
            <Input placeholder="जैसे: कृषि, व्यापार, शिक्षा..." style={{ height: 44, borderRadius: 8 }} />
          </Form.Item>
          <Form.Item label="Sort Order" name="sort_order">
            <InputNumber style={{ width: '100%', height: 44, borderRadius: 8 }} min={0} placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Sub-sector Modal ── */}
      <Modal open={subOpen} onCancel={() => setSubOpen(false)} centered
        title={<span style={{ color: '#7a0000', fontWeight: 700 }}>
          {editingSub ? 'Edit Sub-sector' : `Add Sub-sector`}
          {activeSector && <span style={{ fontSize: 12, color: '#888', fontWeight: 400 }}> — {activeSector.name}</span>}
        </span>}
        footer={[
          <Button key="c" onClick={() => setSubOpen(false)}>रद्द</Button>,
          <Button key="s" type="primary" loading={subSaving} onClick={saveSub}
            style={{ background: '#7a0000', borderColor: '#7a0000' }}>
            {editingSub ? 'Update' : 'Save'}
          </Button>,
        ]} destroyOnClose>
        <Form form={subForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Sub-sector Name" name="name" rules={[{ required: true, message: 'नाम दर्ज करें' }]}>
            <Input placeholder="जैसे: गेहूं उत्पादन, खुदरा व्यापार..." style={{ height: 44, borderRadius: 8 }} />
          </Form.Item>
          <Form.Item label="Sort Order" name="sort_order">
            <InputNumber style={{ width: '100%', height: 44, borderRadius: 8 }} min={0} placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
}

// ── Main DirectoryMobileApp ───────────────────────────────────────────────────
export default function DirectoryMobileApp({ history }) {
  const [tab, setTab]             = useState('list');
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [sectors, setSectors]     = useState([]);
  const [search, setSearch]       = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [filterSector, setFilterSector] = useState('');
  const [filterCity, setFilterCity]     = useState('');
  const [filterState, setFilterState]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);

  const [viewOpen, setViewOpen]   = useState(false);
  const [viewRecord, setViewRecord] = useState(null);

  const [editOpen, setEditOpen]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);

  const [form] = Form.useForm();

  const canEdit    = hasPermission('/business-directory', 'editPermission');
  const canSectors = hasPermission('/business-directory/sectors', 'readPermission');

  const subOptions = selectedSector
    ? (sectors.find(s => String(s.id) === String(selectedSector))?.sub_sectors || [])
    : [];

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) { window.location.href = '/sign-in'; return; }
    if (!hasPermission('/business-directory', 'readPermission')) {
      window.location.href = '/no-access'; return;
    }
    fetchSectors();
  }, []);

  useEffect(() => { if (tab === 'list') fetchData(); }, [search, filterSector, filterCity, filterState, filterStatus, page, tab]);

  const fetchSectors = async () => {
    try {
      const res = await axios.get(`${baseUrl}/business-sector/list`, { headers: authHeader() });
      if (res.data.status) setSectors(res.data.result || []);
    } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search)        params.search              = search;
      if (filterSector)  params.sector_id           = filterSector;
      if (filterCity)    params.city                = filterCity;
      if (filterState)   params.state               = filterState;
      if (filterStatus)  params.verification_status = filterStatus;
      const res = await axios.get(`${baseUrl}/business-directory/list`, { headers: authHeader(), params });
      if (res.data.status) {
        setData((res.data.result || []).map((r, i) => ({ ...r, key: r.id || i })));
        setTotal(res.data.totalCount || 0);
      }
    } catch {
      notification.error({ message: 'Data load failed', placement: 'top' });
    } finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditingId(null); setSelectedSector(null);
    form.resetFields(); setEditOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setSelectedSector(record.sector_id ? String(record.sector_id) : null);
    form.setFieldsValue({
      name: record.name, father_name: record.father_name, phone_no: record.phone_no,
      sector: record.sector_id ? String(record.sector_id) : undefined,
      sub_sector: record.sub_sector_id ? String(record.sub_sector_id) : undefined,
      firm_name: record.firm_name, establishment_year: record.establishment_year,
      business_about: record.business_about, business_address: record.business_address,
      village: record.village, tehsil: record.tehsil, district: record.district,
      dob: record.dob && record.dob !== '0000-00-00' ? moment(record.dob) : null,
      gotra: record.gotra, email: record.email, alternate_phone: record.alternate_phone,
      current_city: record.current_city, state: record.state,
      profile_photo: record.profile_photo, business_photo: record.business_photo,
      verification_status: record.verification_status, admin_notes: record.admin_notes,
    });
    setEditOpen(true);
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const sectorObj  = sectors.find(s => String(s.id) === String(values.sector));
      const sectorLabel = sectorObj?.name || '';
      const subLabel    = (sectorObj?.sub_sectors || []).find(s => String(s.id) === String(values.sub_sector))?.name || '';
      const payload = {
        name: values.name, father_name: values.father_name || null,
        phone_no: values.phone_no,
        sector_id: values.sector || null, sector_label: sectorLabel,
        sub_sector_id: values.sub_sector || null, sub_sector_label: subLabel,
        firm_name: values.firm_name || null, establishment_year: values.establishment_year || null,
        business_about: values.business_about || null, business_address: values.business_address || null,
        village: values.village || null, tehsil: values.tehsil || null, district: values.district || null,
        dob: values.dob ? moment(values.dob).format('YYYY-MM-DD') : null,
        gotra: values.gotra || null, email: values.email || null,
        alternate_phone: values.alternate_phone || null,
        current_city: values.current_city || null, state: values.state || null,
        profile_photo: values.profile_photo || null, business_photo: values.business_photo || null,
        verification_status: values.verification_status || 'Pending',
        admin_notes: values.admin_notes || null,
      };
      if (editingId) {
        await axios.patch(`${baseUrl}/business-directory/update/${editingId}`, payload, { headers: authHeader() });
        notification.success({ message: 'अपडेट हो गया', placement: 'top' });
      } else {
        await axios.post(`${baseUrl}/business-directory/public-submit`, payload);
        notification.success({ message: 'एंट्री जोड़ी गई', placement: 'top' });
      }
      setEditOpen(false); fetchData();
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: e?.response?.data?.message || 'Error', placement: 'top' });
    } finally { setSaving(false); }
  };


  const logout = () => {
    ['accessToken','permission','staffId','staffName'].forEach(k => localStorage.removeItem(k));
    window.location.href = '/sign-in';
  };

  const hasActiveFilter = filterSector || filterCity || filterState || filterStatus;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: 480, margin: '0 auto', position: 'relative' }}>

      {/* ── Sticky Header ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100,
        background: '#7a0000', padding: '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, color: '#d4a84b', fontWeight: 700, letterSpacing: 1 }}>ABRSVS</div>
          <div style={{ fontSize: 15, color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>Business Directory</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {total > 0 && <Badge count={total} style={{ background: '#d4a84b' }} />}
          <MobileShareButton />
          <Button size="small" icon={<LogoutOutlined />} onClick={logout}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', borderRadius: 8 }} />
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display: 'flex', background: '#7a0000', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        {[
          { key: 'list',    icon: <UnorderedListOutlined />, label: 'Submissions' },
          ...(canSectors ? [{ key: 'sectors', icon: <AppstoreOutlined />, label: 'Sectors' }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ flex: 1, padding: '8px 0', background: 'transparent',
              border: 'none', color: tab === t.key ? '#d4a84b' : 'rgba(255,255,255,0.5)',
              borderBottom: tab === t.key ? '2px solid #d4a84b' : '2px solid transparent',
              cursor: 'pointer', fontSize: 11, fontWeight: tab === t.key ? 700 : 400 }}>
            <div style={{ fontSize: 16, marginBottom: 2 }}>{t.icon}</div>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Submissions Tab ── */}
      {tab === 'list' && (
        <div style={{ padding: '12px 12px 100px' }}>

          {/* Search + filter toggle */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <Input prefix={<SearchOutlined style={{ color: '#999' }} />}
              placeholder="नाम, मोबाइल, फर्म, शहर..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              allowClear style={{ flex: 1, borderRadius: 10, height: 40 }} />
            <Button onClick={() => setShowFilter(f => !f)} icon={<FilterOutlined />}
              style={{ height: 40, borderRadius: 10, minWidth: 44,
                borderColor: hasActiveFilter ? '#d4a84b' : '#d9d9d9',
                color: hasActiveFilter ? '#d4a84b' : '#666',
                background: hasActiveFilter ? '#fffbe6' : '#fff',
                fontWeight: hasActiveFilter ? 700 : 400 }}>
              {hasActiveFilter ? `${[filterSector,filterCity,filterState,filterStatus].filter(Boolean).length}` : ''}
            </Button>
          </div>

          {/* Expanded filters */}
          {showFilter && (
            <div style={{ background: '#fff', borderRadius: 10, padding: 12, marginBottom: 10,
              border: '1px solid #e8eaf0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <Select placeholder="व्यवसाय क्षेत्र" allowClear value={filterSector || undefined}
                onChange={v => { setFilterSector(v || ''); setPage(1); }}
                style={{ width: '100%', marginBottom: 8 }} showSearch optionFilterProp="children">
                {sectors.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
              </Select>
              <Input prefix={<EnvironmentOutlined style={{ color: '#999' }} />}
                placeholder="शहर (जैसे: हैदराबाद)" value={filterCity}
                onChange={e => { setFilterCity(e.target.value); setPage(1); }}
                allowClear style={{ marginBottom: 8, borderRadius: 8 }} />
              <Input prefix={<GlobalOutlined style={{ color: '#999' }} />}
                placeholder="राज्य (जैसे: राजस्थान)" value={filterState}
                onChange={e => { setFilterState(e.target.value); setPage(1); }}
                allowClear style={{ marginBottom: 8, borderRadius: 8 }} />
              <Select placeholder="स्थिति" allowClear value={filterStatus || undefined}
                onChange={v => { setFilterStatus(v || ''); setPage(1); }}
                style={{ width: '100%', marginBottom: 8 }}>
                <Option value="Pending">⏳ Pending</Option>
                <Option value="Verified">✅ Verified</Option>
                <Option value="Rejected">❌ Rejected</Option>
              </Select>
              {hasActiveFilter && (
                <Button block danger size="small" onClick={() => {
                  setFilterSector(''); setFilterCity(''); setFilterState(''); setFilterStatus(''); setPage(1);
                }}>Clear All Filters</Button>
              )}
            </div>
          )}

          {/* Results count */}
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            {loading ? 'लोड हो रहा है...' : `${total} entries`}
          </div>

          {/* Cards */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
          ) : data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: 14 }}>
              कोई डेटा नहीं
            </div>
          ) : (
            data.map(r => (
              <EntryCard key={r.id} r={r}
                onView={rec => { setViewRecord(rec); setViewOpen(true); }} />
            ))
          )}

          {/* Pagination */}
          {total > 20 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)} size="small">← Prev</Button>
              <span style={{ lineHeight: '24px', fontSize: 12 }}>Page {page}</span>
              <Button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} size="small">Next →</Button>
            </div>
          )}
        </div>
      )}

      {/* ── Sectors Tab ── */}
      {tab === 'sectors' && <SectorsMobileTab sectors={sectors} onRefresh={fetchSectors} />}

      {/* ── FAB: Add Entry ── */}
      {canEdit && tab === 'list' && (
        <button onClick={openAdd}
          style={{ position: 'fixed', bottom: 28, right: 20, width: 56, height: 56,
            borderRadius: '50%', background: '#d4a84b', border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(212,168,75,0.45)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 24 }}>
          <PlusOutlined />
        </button>
      )}

      {/* ── View Drawer ── */}
      <Drawer
        open={viewOpen} onClose={() => setViewOpen(false)}
        placement="bottom" height="92vh"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setViewOpen(false)}
              style={{ border: 'none', boxShadow: 'none', padding: 0 }} />
            <span style={{ fontWeight: 700, color: '#7a0000' }}>विवरण</span>
          </div>
        }
        footer={canEdit && viewRecord ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button block
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', borderRadius: 8, height: 44 }}
              onClick={() => { setViewOpen(false); openEdit(viewRecord); }}>
              Edit Entry
            </Button>
          </div>
        ) : null}
        destroyOnClose closable={false}
        bodyStyle={{ padding: '0 16px', overflowY: 'auto' }}>
        {viewRecord && <DetailCard r={viewRecord} />}
      </Drawer>

      {/* ── Add / Edit Drawer ── */}
      <Drawer
        open={editOpen} onClose={() => setEditOpen(false)}
        placement="bottom" height="96vh"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setEditOpen(false)}
              style={{ border: 'none', boxShadow: 'none', padding: 0 }} />
            <span style={{ fontWeight: 700, color: '#7a0000' }}>
              {editingId ? 'Edit Entry' : 'Add New Entry'}
            </span>
          </div>
        }
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button block onClick={() => setEditOpen(false)} style={{ height: 44, borderRadius: 8 }}>रद्द करें</Button>
            <Button block type="primary" loading={saving} onClick={onSave}
              style={{ height: 44, borderRadius: 8, background: '#7a0000', borderColor: '#7a0000' }}>
              {editingId ? 'Update' : 'Save'}
            </Button>
          </div>
        }
        destroyOnClose closable={false}
        bodyStyle={{ padding: '0 16px 16px', overflowY: 'auto' }}>

        <Form form={form} layout="vertical" style={{ paddingTop: 12 }}>

          <Sec label="Photos (फोटो)" />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <Item name="profile_photo" noStyle>
              <PhotoUpload />
            </Item>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>दुकान / व्यवसाय की फोटो</div>
              <Item name="business_photo" noStyle>
                <MultiPhotoUpload />
              </Item>
            </div>
          </div>

          <Sec label="Personal Info (व्यक्तिगत)" />
          <Item label="पूरा नाम *" name="name" rules={[{ required: true }, { validator: validateName('पूरा नाम') }]}>
            <Input prefix={<UserOutlined />} placeholder="पूरा नाम" maxLength={100} style={{ height: 44, borderRadius: 8 }} />
          </Item>
          <Item label="पिता का नाम" name="father_name" rules={[{ validator: validateName('पिता का नाम') }]}>
            <Input prefix={<UserOutlined />} placeholder="पिता का नाम" maxLength={100} style={{ height: 44, borderRadius: 8 }} />
          </Item>
          <Item label="WhatsApp / मोबाइल *" name="phone_no" rules={phoneRules}>
            <Input prefix={<PhoneOutlined />} placeholder="10-digit number" maxLength={10}
              style={{ height: 44, borderRadius: 8 }}
              onKeyDown={e => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault(); }} />
          </Item>
          <Item label="वैकल्पिक नंबर" name="alternate_phone">
            <Input prefix={<PhoneOutlined />} placeholder="दूसरा नंबर" maxLength={10}
              style={{ height: 44, borderRadius: 8 }}
              onKeyDown={e => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault(); }} />
          </Item>
          <Item label="जन्म तिथि" name="dob">
            <DatePicker style={{ width: '100%', height: 44, borderRadius: 8 }} format="DD-MM-YYYY"
              placeholder="DD-MM-YYYY" disabledDate={d => d && d.isAfter(moment())} />
          </Item>
          <Item label="गोत्र" name="gotra">
            <Input prefix={<UserOutlined />} placeholder="गोत्र" maxLength={100} style={{ height: 44, borderRadius: 8 }} />
          </Item>
          <Item label="ईमेल" name="email" rules={[{ type: 'email', message: 'Valid email required' }]}>
            <Input placeholder="email@example.com" maxLength={255} style={{ height: 44, borderRadius: 8 }} />
          </Item>

          <Sec label="Business / Profession (व्यवसाय)" />
          <Item label="मुख्य क्षेत्र" name="sector">
            <Select placeholder="क्षेत्र चुनें" showSearch optionFilterProp="children" style={{ borderRadius: 8 }}
              onChange={v => { setSelectedSector(v); form.setFieldsValue({ sub_sector: undefined }); }}>
              {sectors.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
            </Select>
          </Item>
          <Item label="उप-क्षेत्र" name="sub_sector">
            <Select placeholder={selectedSector ? 'उप-क्षेत्र चुनें' : 'पहले मुख्य क्षेत्र चुनें'}
              disabled={!selectedSector} showSearch optionFilterProp="children">
              {subOptions.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
            </Select>
          </Item>
          <Item label="फर्म / दुकान का नाम" name="firm_name" rules={[{ validator: validateSafe('फर्म/दुकान') }]}>
            <Input prefix={<ShopOutlined />} placeholder="फर्म / दुकान का नाम" maxLength={200}
              style={{ height: 44, borderRadius: 8 }} />
          </Item>
          <Item label="स्थापना वर्ष" name="establishment_year"
            rules={[{ type: 'number', min: 1900, max: new Date().getFullYear() }]}>
            <InputNumber style={{ width: '100%', height: 44, borderRadius: 8 }} placeholder="जैसे: 2010"
              min={1900} max={new Date().getFullYear()} />
          </Item>
          <Item label="व्यावसायिक पता" name="business_address" rules={[{ validator: validateSafe('पता') }]}>
            <TextArea rows={2} placeholder="दुकान/ऑफिस का पूरा पता" maxLength={500}
              style={{ borderRadius: 8 }} />
          </Item>
          <Item label="व्यवसाय का शहर *" name="current_city"
            rules={[{ required: true, message: 'शहर दर्ज करें' }, { validator: validateName('शहर') }]}>
            <Input prefix={<EnvironmentOutlined />} placeholder="जैसे: हैदराबाद, मुंबई..."
              maxLength={100} style={{ height: 44, borderRadius: 8 }} />
          </Item>
          <Item label="राज्य *" name="state"
            rules={[{ required: true, message: 'राज्य दर्ज करें' }, { validator: validateName('राज्य') }]}>
            <Input prefix={<GlobalOutlined />} placeholder="जैसे: राजस्थान, तेलंगाना..."
              maxLength={100} style={{ height: 44, borderRadius: 8 }} />
          </Item>
          <Item label="व्यवसाय के बारे में" name="business_about" rules={[{ validator: validateSafe('विवरण') }]}>
            <TextArea rows={3} placeholder="व्यवसाय, सेवाओं या उत्पादों के बारे में..." maxLength={600}
              style={{ borderRadius: 8 }} />
          </Item>

          <Sec label="Native Location (मूल स्थान)" />
          <Item label="गाँव *" name="village" rules={[{ required: true }, { validator: validateName('गाँव') }]}>
            <Input prefix={<EnvironmentOutlined />} placeholder="मूल गाँव" maxLength={100}
              style={{ height: 44, borderRadius: 8 }} />
          </Item>
          <Item label="तहसील *" name="tehsil" rules={[{ required: true }, { validator: validateName('तहसील') }]}>
            <Input prefix={<EnvironmentOutlined />} placeholder="तहसील" maxLength={100}
              style={{ height: 44, borderRadius: 8 }} />
          </Item>
          <Item label="जिला *" name="district" rules={[{ required: true }, { validator: validateName('जिला') }]}>
            <Input prefix={<EnvironmentOutlined />} placeholder="जिला" maxLength={100}
              style={{ height: 44, borderRadius: 8 }} />
          </Item>

          {canEdit && (
            <>
              <Sec label="Admin Fields" />
              <Item label="Verification Status" name="verification_status" initialValue="Pending">
                <Select style={{ borderRadius: 8 }}>
                  <Option value="Pending">⏳ Pending</Option>
                  <Option value="Verified">✅ Verified</Option>
                  <Option value="Rejected">❌ Rejected</Option>
                </Select>
              </Item>
              <Item label="Admin Notes" name="admin_notes" rules={[{ validator: validateSafe('Notes') }]}>
                <TextArea rows={2} placeholder="Internal notes..." maxLength={500} style={{ borderRadius: 8 }} />
              </Item>
            </>
          )}

        </Form>
      </Drawer>

    </div>
  );
}
